import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type * as Highcharts from "highcharts/highstock";
import BaseConditionMonitoringChart from "../../components/charts/BaseConditionMonitoringChart";
import { useDashboardSync } from "../DashboardSyncContext";

export interface SeriesDef {
  /** Key into the `data` map. */
  key: string;
  name: string;
  unit?: string;
  color?: string;
  type?: "line" | "areaspline";
  /** Index into the `yAxes` prop. Defaults to 0 (single shared axis). */
  axis?: number;
  /** Highlight individual data points with circle markers. Highcharts only
   * draws them where points have room to breathe (e.g. once zoomed in) and
   * hides them again on dense/zoomed-out views, so the line stays clean by
   * default. Defaults to false (no markers, previous behavior). */
  showMarkers?: boolean;
}

/** One y-axis definition for multi-axis charts (motor). */
export interface AxisDef {
  /** Axis title / unit label, e.g. "°C". */
  title?: string;
  color?: string;
  /** Render on the right-hand side. */
  opposite?: boolean;
  /** Baseline scale: the axis spans at least this range but expands for
   * outliers, so a flat/near-constant series still shows a readable scale. */
  softMin?: number;
  softMax?: number;
  /** Flip the axis (high at bottom). Used for the mirrored dual-area look. */
  reversed?: boolean;
}

interface SeriesChartProps {
  title?: string;
  seriesDefs: SeriesDef[];
  /** Optional multi-axis config. When omitted a single default y-axis is used. */
  yAxes?: AxisDef[];
  /** Per-key points. New Map reference on each update triggers a re-seed. */
  data: Map<string, { t: number; v: number }[]>;
  height?: number;
  /** Show the "Full Resolution" action when zoomed (Doris/hybrid sources). */
  canDetail?: boolean;
  onDetail?: (startMs: number, endMs: number) => void | Promise<void>;
  commentType?: string;
  machineBK?: string;
  className?: string;
}

const DEFAULT_COLORS = [
  "#3b82f6",
  "#ef4444",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
];

/**
 * Presentational time-series chart: renders one Highcharts series per
 * {@link SeriesDef}, fed imperatively from `data`. All data fetching lives in
 * the caller (`usePanelData`); all cross-chart linking lives in
 * {@link useDashboardSync}. Used by both TimeSeriesPanel and the energy screen
 * so there's a single charting + zoom/hover/full-resolution implementation.
 */
const SeriesChart: React.FC<SeriesChartProps> = ({
  title,
  seriesDefs,
  yAxes,
  data,
  height = 300,
  canDetail = false,
  onDetail,
  commentType,
  machineBK,
  className,
}) => {
  const sync = useDashboardSync();
  const chartRef = useRef<Highcharts.Chart | null>(null);
  const unregisterRef = useRef<(() => void) | null>(null);
  // True while WE are re-seeding data / applying extremes, so the axis'
  // afterSetExtremes can tell our own programmatic changes apart from genuine
  // user zoom / navigator / pan interactions.
  const applyingRef = useRef(false);
  const [ready, setReady] = useState(0);
  const [zoomRange, setZoomRange] = useState<{
    min: number;
    max: number;
  } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const options = useMemo<Highcharts.Options>(() => {
    const hcSeries = seriesDefs.map((d, i): Highcharts.SeriesOptionsType => {
      const color = d.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length];
      const base = {
        name: d.name,
        color,
        data: [] as [number, number][],
        marker: d.showMarkers
          ? { enabled: true, radius: 3, symbol: "circle", enabledThreshold: 0 }
          : { enabled: false },
        tooltip: { valueSuffix: d.unit ? ` ${d.unit}` : "" },
        animation: false,
        ...(yAxes && yAxes.length > 0 ? { yAxis: d.axis ?? 0 } : {}),
      };
      if (d.type === "areaspline") {
        return {
          ...base,
          type: "area",
          lineWidth: 2,
          fillOpacity: 0.25,
          step: "left",
        } as Highcharts.SeriesAreaOptions;
      }
      return {
        ...base,
        type: "line",
        lineWidth: 1.5,
        step: "left",
      } as Highcharts.SeriesLineOptions;
    });

    // Single shared axis by default; explicit multi-axis config when provided.
    const yAxis: Highcharts.YAxisOptions | Highcharts.YAxisOptions[] =
      yAxes && yAxes.length > 0
        ? yAxes.map((ax) => ({
            title: ax.title
              ? {
                  text: ax.title,
                  style: ax.color ? { color: ax.color } : undefined,
                }
              : { text: undefined },
            labels: {
              enabled: true,
              style: ax.color ? { color: ax.color } : undefined,
            },
            opposite: ax.opposite ?? false,
            reversed: ax.reversed ?? false,
            softMin: ax.softMin,
            softMax: ax.softMax,
            startOnTick: true,
            endOnTick: true,
            tickPixelInterval: 36,
            showEmpty: false,
          }))
        : {
            title: { text: undefined },
            startOnTick: true,
            endOnTick: true,
            tickPixelInterval: 36,
          };

    return {
      chart: {
        height,
        backgroundColor: "transparent",
        animation: false,
        // Drag-select zooms, shift+drag pans, pinch zooms on touch.
        // Wheel zoom is handled by our own listener below rather than
        // `zooming.mouseWheel`, because that option needs the separate
        // mouse-wheel-zoom module which can't be reliably attached to the UMD
        // `highcharts/highstock` instance this chart is built with.
        panKey: "shift",
        panning: { enabled: true, type: "x" },
        zooming: { type: "x", pinchType: "x" },
      },
      time: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      title: title
        ? {
            text: title,
            align: "left",
            style: { fontSize: "14px", fontWeight: "600" },
          }
        : { text: undefined },
      rangeSelector: { enabled: false },
      navigator: { enabled: false },
      scrollbar: { enabled: false },
      credits: { enabled: false },
      legend: {
        enabled: seriesDefs.length > 1,
        align: "center",
        verticalAlign: "bottom",
        // With many series (e.g. every servo axis overlaid) an uncapped legend
        // eats most of `chart.height` and leaves a sliver of plot area, so cap
        // it and let Highcharts paginate instead.
        ...(seriesDefs.length > 8 ? { maxHeight: 84 } : {}),
      },
      xAxis: {
        type: "datetime",
        ordinal: false,
        crosshair: { width: 1, color: "#9ca3af", dashStyle: "Dash" },
        events: {
          afterSetExtremes: function (
            e: Highcharts.AxisSetExtremesEventObject,
          ) {
            // Ignore extreme changes we caused ourselves while re-seeding data
            // (a live flush auto-rescale) or those applied by the sync cascade
            // to the other charts — only a genuine user interaction on THIS
            // chart should drive the shared window.
            if (applyingRef.current || sync?.isSyncing()) return;
            const { min, max, dataMin, dataMax } = e;
            const zoomed =
              typeof min === "number" &&
              typeof max === "number" &&
              typeof dataMin === "number" &&
              typeof dataMax === "number" &&
              max - min < (dataMax - dataMin) * 0.98;
            setZoomRange(zoomed ? { min: min!, max: max! } : null);
            // User-driven zoom / navigator / pan → share the window (or clear
            // it to full range when zoomed back out).
            sync?.syncExtremes(
              zoomed ? min : undefined,
              zoomed ? max : undefined,
            );
          },
        },
      },
      yAxis,
      plotOptions: { series: { animation: false, turboThreshold: 0 } },
      series: hcSeries,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesDefs, yAxes, height, title]);

  const handleReady = useCallback(
    (chart: Highcharts.Chart) => {
      chartRef.current = chart;
      unregisterRef.current = sync?.register(chart) ?? null;
      setReady((v) => v + 1);
    },
    [sync],
  );

  useEffect(
    () => () => {
      unregisterRef.current?.();
    },
    [],
  );

  // Feed data; preserve the shared zoom window across the re-seed.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    applyingRef.current = true;
    try {
      seriesDefs.forEach((d, i) => {
        const pts = data.get(d.key) ?? [];
        chart.series?.[i]?.setData(
          pts.map((p) => [p.t, p.v]) as [number, number][],
          false,
          false,
          false,
        );
      });
      const view = sync?.getViewRange();
      const axis = chart.xAxis?.[0];
      if (axis && view) {
        // Preserve the user's shared zoom window across the re-seed.
        axis.setExtremes(view.min, view.max, true, false);
      } else if (axis) {
        // No user zoom → follow the data. Reset to full range so the view grows
        // with new live points instead of staying stuck on the first point's
        // tiny window (redraw alone keeps stale extremes).
        axis.setExtremes(undefined, undefined, true, false);
      } else {
        chart.redraw(false);
      }
    } finally {
      applyingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ready, seriesDefs]);

  /**
   * Mouse-wheel zoom on the x-axis, anchored at the cursor. Routed through
   * `sync.syncExtremes` so every linked chart zooms together (same path a
   * drag-select takes); falls back to setting our own extremes when rendered
   * outside a provider. Zooming back out past the full data range clears the
   * shared window instead of clamping.
   */
  useEffect(() => {
    const chart = chartRef.current;
    const container = chart?.container;
    if (!chart || !container) return;

    const onWheel = (e: WheelEvent) => {
      const axis = chartRef.current?.xAxis?.[0];
      if (!axis) return;
      const { min, max, dataMin, dataMax } = axis.getExtremes();
      if (typeof dataMin !== "number" || typeof dataMax !== "number") return;

      const curMin = typeof min === "number" ? min : dataMin;
      const curMax = typeof max === "number" ? max : dataMax;
      const curRange = curMax - curMin;
      const fullRange = dataMax - dataMin;
      if (!(curRange > 0) || !(fullRange > 0)) return;

      // Inside the plot area only, so wheeling over the legend/navigator still
      // scrolls the page.
      const rect = container.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      if (
        px < chart.plotLeft ||
        px > chart.plotLeft + chart.plotWidth ||
        py < chart.plotTop ||
        py > chart.plotTop + chart.plotHeight
      ) {
        return;
      }
      e.preventDefault();

      const factor = e.deltaY < 0 ? 1 / 1.25 : 1.25;
      const newRange = curRange * factor;

      // Zoomed out to (or past) the full range → back to full view.
      // `zoomRange` (which gates the Full Resolution button) is set here rather
      // than in afterSetExtremes: that handler ignores sync-driven changes.
      if (newRange >= fullRange) {
        setZoomRange(null);
        if (sync) sync.syncExtremes(undefined, undefined);
        else axis.setExtremes(undefined, undefined, true, false);
        return;
      }

      // Keep the timestamp under the cursor fixed while scaling.
      const anchor = axis.toValue(px, false);
      const ratio = (anchor - curMin) / curRange;
      let newMin = anchor - newRange * ratio;
      let newMax = newMin + newRange;
      if (newMin < dataMin) {
        newMin = dataMin;
        newMax = newMin + newRange;
      }
      if (newMax > dataMax) {
        newMax = dataMax;
        newMin = newMax - newRange;
      }

      setZoomRange({ min: newMin, max: newMax });
      if (sync) sync.syncExtremes(newMin, newMax);
      else axis.setExtremes(newMin, newMax, true, false);
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, [ready, sync]);

  const handleFullResolution = useCallback(async () => {
    if (!zoomRange || !onDetail) return;
    setDetailLoading(true);
    try {
      await onDetail(zoomRange.min, zoomRange.max);
    } finally {
      setDetailLoading(false);
    }
  }, [zoomRange, onDetail]);

  return (
    <div className={`relative w-full h-full ${className ?? ""}`}>
      {canDetail && zoomRange && onDetail && (
        <button
          disabled={detailLoading}
          onClick={handleFullResolution}
          /* Sits left of BaseConditionMonitoringChart's "Reset Zoom" button
             (top-4 right-4, z-20) — both only show while zoomed, so they'd
             otherwise stack and Reset Zoom would cover this one. */
          className="absolute top-4 right-32 z-20 flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg border shadow-lg bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-300 disabled:opacity-60"
        >
          {detailLoading ? "Loading…" : "Full Resolution"}
        </button>
      )}
      <BaseConditionMonitoringChart
        options={options}
        onChartReady={handleReady}
        className="w-full h-full"
        commentProps={
          commentType ? { chartType: commentType, machineBK } : undefined
        }
      />
    </div>
  );
};

export default SeriesChart;
